import React, { useState, useEffect, useCallback } from 'react';
import { AUTO_DETECT_MAPPING } from '../constants/autoDetectMapping';
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  Badge,
  Icon,
  useColorModeValue,
  AspectRatio,
  Image as ChakraImage, // Alias the Chakra UI Image component
  Wrap,
  WrapItem,
  Tooltip,
  IconButton,
  Tag,
  TagLabel,
  TagLeftIcon,
  Divider,
  Skeleton,
  useToken,
} from '@chakra-ui/react';
import { LazyImage } from '../utils/lazyLoadUtils';
import { useNavigate } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon, CloseIcon } from '@chakra-ui/icons';
import {
  MapPin,
  Clock,
  Image as ImageIcon,
  VideoCamera, // Added for video count
  Cube,
  Flag,
  MapTrifold,
  Calendar,
  ArrowSquareOut,
} from '@phosphor-icons/react';
import ImagePreviewModal from './ImagePreviewModal';
import { mapFeature, normalizeFeature, getMediaFeatures, formatFeatureName } from '../utils/featureUtils'; // Import formatFeatureName

function SiteCardComponent({ site, selectedSublocation, onMediaClick, onFeatureFilter, isInInfoWindow, isMobile, filters, showFilteredMedia, filteredMedia, onClose }) {
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const [preloadedImages, setPreloadedImages] = useState({});
  const [isCarouselHovered, setIsCarouselHovered] = useState(false);

  // Ancient map aesthetic colors
  const bgColor = useColorModeValue('ancient.parchment.light', 'ancient.parchment.dark');
  const borderColor = useColorModeValue('ancient.border.light', 'ancient.border.dark');
  const textColor = useColorModeValue('ancient.ink', 'ancient.ink.light');
  const buttonBg = useColorModeValue('rgba(255, 253, 245, 0.8)', 'rgba(30, 25, 20, 0.8)');
  const buttonColor = useColorModeValue('ancient.ink', 'ancient.gold');
  const accentColor = useColorModeValue('ancient.terracotta', 'ancient.gold');
  const subtleTextColor = useColorModeValue('ancient.ink.subtle', 'ancient.gold.subtle');
  const subtleBg = useColorModeValue('rgba(210, 190, 160, 0.2)', 'rgba(60, 50, 40, 0.3)');
  
  // Feature badge colors
  const defaultBadgeBg = useColorModeValue('ancient.subtle', 'gray.700');
  const selectedBadgeBg = useColorModeValue('gray.200', 'gray.600');
  const badgeTextColor = useColorModeValue('gray.800', 'white');

  // Always use site.media for display within the card
  const displayMedia = site.media || []; // Used for total counts, etc.
  
  // Determine the source for the image carousel
  // If filters are active and filteredMedia is provided, use it. Otherwise, use all site media.
  const carouselMediaSource = showFilteredMedia && filteredMedia ? filteredMedia : displayMedia;
  // Include both images and videos in the carousel
  const carouselItems = carouselMediaSource.filter(m => m?.url?.match(/^https?:\/\/.+/) && (m?.type === 'image' || m?.type === 'video'));

  // Reset current image index when carouselItems array changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [carouselItems.map(item => item.url).join(',')]); // More stable dependency

  // Preload adjacent images when the current image index changes (only for images)
  useEffect(() => {
    if (carouselItems.length <= 1) return;
    
    const preloadImage = (item) => {
      if (item?.type === 'image' && item?.url && !preloadedImages[item.url]) {
        const img = new window.Image();
        img.src = item.url;
        setPreloadedImages(prev => ({ ...prev, [item.url]: true }));
      }
    };

    // Preload next item if it's an image
    const nextIndex = (currentImageIndex + 1) % carouselItems.length;
    preloadImage(carouselItems[nextIndex]);
    
    // Preload previous item if it's an image
    const prevIndex = (currentImageIndex + carouselItems.length - 1) % carouselItems.length;
    preloadImage(carouselItems[prevIndex]);
    
  }, [currentImageIndex, carouselItems, preloadedImages]);

  const has3DModel = displayMedia.some(m => m.type === '3d');
  const photoCount = displayMedia.filter(m => m.type === 'image').length;
  const videoCount = displayMedia.filter(m => m.type === 'video').length; // Added video count

  // Get unique features from media, using our utility function
  const getCardFeatures = useCallback(() => {
    // Always process all of site's media to list all its features
    const baseMediaItems = site.media || []; 
    
    // Using Map to avoid duplicates
    const features = new Map();
    
    baseMediaItems.forEach(media => {
      // Get features for this media item
      const mediaFeatures = getMediaFeatures(media);
      
      // Add to our features map
      mediaFeatures.forEach(feature => {
        features.set(feature.id, feature);
      });
    });
    
    const result = Array.from(features.values());
    return result;
  }, [site.media]); // Depend only on site.media

  const enabledFeatures = getCardFeatures();

  const handleCarouselItemClick = (e, mediaItem, index) => {
    e.stopPropagation();
    setCurrentImageIndex(index); // Set index for carousel navigation

    if (mediaItem.type === 'image') {
      if (!mediaItem?.url?.match(/^https?:\/\/.+/)) {
        console.error('Invalid image URL:', mediaItem?.url);
        return;
      }
      // Only use onMediaClick (for external modal) or setIsPreviewOpen (for internal modal) for images
      if (onMediaClick && mediaItem) {
        onMediaClick(mediaItem, index);
      } else {
        setIsPreviewOpen(true);
      }
    }
    // For videos, clicks are handled by native controls or simply focus the item.
  };

  const handleCardClick = (e) => {
    // If click is within the carousel wrapper, or any button, or any badge, do not navigate.
    if (e.target.closest('.site-card-carousel-wrapper') ||
        e.target.closest('button') ||
        e.target.closest('.chakra-badge')) {
      return;
    }
    
    navigate(`/site/${site.id}`);
  };

  const handlePrevImage = (e) => {
    e?.stopPropagation();
    // Prevent state updates if no items
    if (carouselItems.length === 0) {
      return;
    }
    setCurrentImageIndex(currentIndex =>
      currentIndex === 0 ? carouselItems.length - 1 : currentIndex - 1
    );
  };

  const handleNextImage = (e) => {
    e?.stopPropagation();
    // Prevent state updates if no items
    if (carouselItems.length === 0) {
      return;
    }
    setCurrentImageIndex(currentIndex =>
      currentIndex === carouselItems.length - 1 ? 0 : currentIndex + 1
    );
  };

  const handleFeatureClick = (e, feature) => {
    e.stopPropagation();
    if (onFeatureFilter) {
      // Pass the exact feature ID for consistent matching
      onFeatureFilter(feature.id);
    }
  };

  // Check if a feature is selected by comparing exact IDs (no normalization)
  const isFeatureSelected = (feature) => {
    if (!onFeatureFilter || !filters?.features) return false;
    // Use exact matching for feature IDs
    return filters.features.includes(feature.id);
  };

  // Get feature label by first checking metadata then falling back to feature label
  const getFeatureLabel = (feature) => {
    if (filters?.featuresMetadata) {
      const normalizedId = normalizeFeature(feature.id);
      const metadata = Object.values(filters.featuresMetadata).find(f => 
        normalizeFeature(f.id) === normalizedId
      );
      if (metadata) {
        return metadata.label;
      }
    }
    return feature.label || feature.id;
  };

  // Get current item safely
  const currentItem = carouselItems[currentImageIndex];

  // Determine display coordinates
  const mainLat = site?.location?.coordinates?.lat;
  const mainLng = site?.location?.coordinates?.lng;
  const subLat = selectedSublocation?.coordinates?.lat;
  const subLng = selectedSublocation?.coordinates?.lng;

  let displayCoordinatesString = 'Coordinates unavailable';
  if (typeof subLat === 'number' && typeof subLng === 'number') {
    displayCoordinatesString = `${subLat.toFixed(6)}, ${subLng.toFixed(6)}`;
  } else if (typeof mainLat === 'number' && typeof mainLng === 'number') {
    displayCoordinatesString = `${mainLat.toFixed(6)}, ${mainLng.toFixed(6)}`;
  }
  
  return (
    <>
      <Box
        className={isMobile && isInInfoWindow ? 'mobile-popup-container open' : ''}
        w="100%"
        maxW={isInInfoWindow ? (isMobile ? "100%" : "580px") : "100%"}
        bg={isMobile && isInInfoWindow ? useColorModeValue('white', 'gray.800') : bgColor}
        borderRadius={isMobile && isInInfoWindow ? "16px 16px 0 0" : "lg"}
        border="1px"
        borderColor={borderColor}
        overflow="hidden"
        boxShadow={isMobile && isInInfoWindow ? "0 -4px 20px rgba(0, 0, 0, 0.15)" : "0 2px 8px rgba(0, 0, 0, 0.1)"}
        transition="all 0.3s ease"
        position={isMobile && isInInfoWindow ? "fixed" : "relative"}
        bottom={isMobile && isInInfoWindow ? 0 : "auto"}
        left={isMobile && isInInfoWindow ? 0 : "auto"}
        right={isMobile && isInInfoWindow ? 0 : "auto"}
        zIndex={isMobile && isInInfoWindow ? 1000 : "auto"}
        maxHeight={isMobile && isInInfoWindow ? "70vh" : (isInInfoWindow ? "calc(100vh - 200px)" : "auto")}
        overflowY={isMobile && isInInfoWindow ? "auto" : (isInInfoWindow ? "auto" : "visible")}
        paddingBottom={isMobile && isInInfoWindow ? "env(safe-area-inset-bottom)" : "0"}
        _before={!isMobile || !isInInfoWindow ? {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: useColorModeValue(
            "url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxkZWZzPjxwYXR0ZXJuIGlkPSJwYXR0ZXJuIiB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHBhdHRlcm5Vbml0cz0idXNlclNwYWNlT25Vc2UiIHBhdHRlcm5UcmFuc2Zvcm09InJvdGF0ZSg0NSkiPjxyZWN0IHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgZmlsbD0icmdiYSgwLDAsMCwwLjAzKSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3QgZmlsbD0idXJsKCNwYXR0ZXJuKSIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIvPjwvc3ZnPg==')",
            "url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxkZWZzPjxwYXR0ZXJuIGlkPSJwYXR0ZXJuIiB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHBhdHRlcm5Vbml0cz0idXNlclNwYWNlT25Vc2UiIHBhdHRlcm5UcmFuc2Zvcm09InJvdGF0ZSg0NSkiPjxyZWN0IHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjA1KSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3QgZmlsbD0idXJsKCNwYXR0ZXJuKSIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIvPjwvc3ZnPg==')"
          ),
          backgroundSize: "40px 40px",
          opacity: 0.5,
          pointerEvents: 'none'
        } : {}}
        _after={!isMobile || !isInInfoWindow ? {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          border: "1px solid",
          borderColor: useColorModeValue("rgba(0,0,0,0.05)", "rgba(255,255,255,0.05)"),
          borderRadius: "lg",
          pointerEvents: 'none'
        } : {}}
        onClick={handleCardClick}
        cursor="pointer"
        backgroundImage={!isMobile || !isInInfoWindow ? useColorModeValue(
          "linear-gradient(rgba(245, 240, 230, 0.8), rgba(245, 240, 230, 0.8)), url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxkZWZzPjxwYXR0ZXJuIGlkPSJwYXR0ZXJuIiB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHBhdHRlcm5Vbml0cz0idXNlclNwYWNlT25Vc2UiIHBhdHRlcm5UcmFuc2Zvcm09InJvdGF0ZSg0NSkiPjxyZWN0IHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgZmlsbD0icmdiYSgwLDAsMCwwLjAzKSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3QgZmlsbD0idXJsKCNwYXR0ZXJuKSIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIvPjwvc3ZnPg==')",
          "linear-gradient(rgba(40, 35, 30, 0.8), rgba(40, 35, 30, 0.8)), url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxkZWZzPjxwYXR0ZXJuIGlkPSJwYXR0ZXJuIiB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHBhdHRlcm5Vbml0cz0idXNlclNwYWNlT25Vc2UiIHBhdHRlcm5UcmFuc2Zvcm09InJvdGF0ZSg0NSkiPjxyZWN0IHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjA1KSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3QgZmlsbD0idXJsKCNwYXR0ZXJuKSIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIvPjwvc3ZnPg==')"
        ) : 'none'}
        backgroundSize={!isMobile || !isInInfoWindow ? "cover" : 'auto'}
        _hover={isInInfoWindow || (isMobile && isInInfoWindow) ? undefined : {
          transform: 'translateY(-4px)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          borderColor: accentColor,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(210,180,140,0.1) 0%, rgba(0,0,0,0) 20%)',
            zIndex: -1
          }
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Mobile popup handle */}
        {isMobile && isInInfoWindow && (
          <Box className="mobile-popup-handle" />
        )}
        
        {/* View Details CTA that shows on hover */}
        {!isInInfoWindow && (
          <Box
            position="absolute"
            top={3}
            right={3}
            zIndex={2}
            opacity={isHovered ? 1 : 0}
            transform={isHovered ? "translateY(0)" : "translateY(-10px)"}
            transition="all 0.3s ease"
          >
            <HStack
              px={3}
              py={1}
              bg="blackAlpha.700"
              color="white"
              borderRadius="full"
              fontSize="sm"
              fontWeight="medium"
              spacing={1}
            >
              <Text>View Details</Text>
              <Icon as={ArrowSquareOut} weight="bold" />
            </HStack>
          </Box>
        )}

        {carouselItems.length > 0 && currentItem ? (
          <Box
            position="relative"
            className="site-card-carousel-wrapper"
            onMouseEnter={() => setIsCarouselHovered(true)}
            onMouseLeave={() => setIsCarouselHovered(false)}
          >
            {/* Close button for both mobile and desktop when in info window */}
            {isInInfoWindow && (
              <IconButton
                aria-label="Close"
                icon={<CloseIcon />}
                size="sm"
                position="absolute"
                top="12px"
                right="12px"
                zIndex="4" // Higher than carousel controls
                onClick={(e) => {
                  e.stopPropagation();
                  if (onClose) onClose();
                }}
                bg="rgba(0, 0, 0, 0.5)"
                color="white"
                _hover={{ bg: 'rgba(0, 0, 0, 0.7)' }}
                isRound
                border="1px solid rgba(255, 255, 255, 0.2)"
                backdropFilter="blur(4px)"
              />
            )}
            <AspectRatio ratio={16/9}>
              <Skeleton isLoaded={!isLoading} height="100%" width="100%" borderRadius={0}>
                {currentItem?.url?.match(/^https?:\/\/.+/) ? (
                  currentItem.type === 'image' ? (
                    <LazyImage
                      src={currentItem.url}
                      alt={`${site.name} - Image ${currentImageIndex + 1}`}
                      style={{ objectFit: "cover", cursor: "pointer", transition: "transform 0.3s ease" }}
                      onClick={(e) => handleCarouselItemClick(e, currentItem, currentImageIndex)}
                      onLoad={() => setIsLoading(false)}
                      onError={() => setIsLoading(false)}
                      threshold={0.01}
                      key={`card-media-${currentItem.id || currentImageIndex}`}
                      _hover={{ transform: 'scale(1.02)' }}
                    />
                  ) : currentItem.type === 'video' ? (
                    <video
                      src={currentItem.url}
                      controls
                      style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '0px' }}
                      onClick={(e) => e.stopPropagation()} // Prevent card navigation, allow controls
                      onLoadedData={() => setIsLoading(false)}
                      onError={() => { setIsLoading(false); console.error("Error loading video:", currentItem.url);}}
                      key={`card-media-${currentItem.id || currentImageIndex}`}
                    >
                      Your browser does not support the video tag.
                    </video>
                  ) : (
                    <Box bg="gray.700" display="flex" alignItems="center" justifyContent="center" height="100%">
                      <Text color="white">Unsupported media type</Text>
                    </Box>
                  )
                ) : (
                  <Box bg="gray.700" display="flex" alignItems="center" justifyContent="center" height="100%">
                    <Text color="white">Invalid Media URL</Text>
                  </Box>
                )}
              </Skeleton>
            </AspectRatio>

            {carouselItems.length > 1 && (
              <>
                <IconButton
                  icon={<ChevronLeftIcon boxSize={6} />}
                  position="absolute"
                  left={3}
                  top="50%"
                  transform="translateY(-50%)"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePrevImage(e);
                  }}
                  bg="blackAlpha.600"
                  color="white"
                  _hover={{ 
                    bg: "blackAlpha.800", 
                    transform: 'translateY(-50%) scale(1.1)' 
                  }}
                  _active={{ 
                    bg: "blackAlpha.900", 
                    transform: 'translateY(-50%) scale(0.9)' 
                  }}
                  size="sm"
                  isRound
                  aria-label="Previous image"
                  zIndex={3}
                  opacity={isCarouselHovered ? 0.8 : 0.3}
                  transition="all 0.2s ease-in-out"
                  backdropFilter="blur(4px)"
                  border="1px solid"
                  borderColor="whiteAlpha.300"
                />
                <IconButton
                  icon={<ChevronRightIcon boxSize={6} />}
                  position="absolute"
                  right={3}
                  top="50%"
                  transform="translateY(-50%)"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNextImage(e);
                  }}
                  bg="blackAlpha.600"
                  color="white"
                  _hover={{ 
                    bg: "blackAlpha.800", 
                    transform: 'translateY(-50%) scale(1.1)' 
                  }}
                  _active={{ 
                    bg: "blackAlpha.900", 
                    transform: 'translateY(-50%) scale(0.9)' 
                  }}
                  size="sm"
                  isRound
                  aria-label="Next image"
                  zIndex={3}
                  opacity={isCarouselHovered ? 0.8 : 0.3}
                  transition="all 0.2s ease-in-out"
                  backdropFilter="blur(4px)"
                  border="1px solid"
                  borderColor="whiteAlpha.300"
                />

                {/* Left click zone for navigation */}
                <Box
                  position="absolute"
                  left={0}
                  top={0}
                  bottom={0}
                  width="15%"
                  cursor="pointer"
                  zIndex={1}
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePrevImage(e);
                  }}
                  _hover={{
                    bg: 'blackAlpha.100'
                  }}
                  transition="background 0.2s ease-in-out"
                />

                {/* Right click zone for navigation */}
                <Box
                  position="absolute"
                  right={0}
                  top={0}
                  bottom={0}
                  width="15%"
                  cursor="pointer"
                  zIndex={1}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNextImage(e);
                  }}
                  _hover={{
                    bg: 'blackAlpha.100'
                  }}
                  transition="background 0.2s ease-in-out"
                />

                {/* Enhanced image indicator dots */}
                <HStack
                  position="absolute"
                  bottom={3}
                  left="50%"
                  transform="translateX(-50%)"
                  spacing={1.5}
                  bg="blackAlpha.600"
                  py={1.5}
                  px={3}
                  borderRadius="full"
                  zIndex={1}
                >
                  {carouselItems.map((_, index) => (
                    <Box
                      key={index}
                      w={2.5}
                      h={2.5}
                      borderRadius="full"
                      bg={index === currentImageIndex ? "white" : "whiteAlpha.600"}
                      cursor="pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        console.log('[SiteCard] Dot indicator clicked. Setting index to:', index);
                        setCurrentImageIndex(index);
                      }}
                      transition="all 0.2s"
                      _hover={{ transform: "scale(1.2)" }}
                    />
                  ))}
                </HStack>
              </>
            )}
          </Box>
        ) : showFilteredMedia && displayMedia.length > 0 ? (
          <Box
            position="relative"
            className="site-card-carousel-wrapper"
            onMouseEnter={() => setIsCarouselHovered(true)}
            onMouseLeave={() => setIsCarouselHovered(false)}
          >
            <AspectRatio ratio={16/9}>
              <Box 
                bg={subtleBg} 
                display="flex" 
                alignItems="center" 
                justifyContent="center"
                flexDirection="column"
                gap={2}
              >
                <Icon as={ImageIcon} color={accentColor} boxSize={8} />
                <Text color={textColor} fontSize="sm" textAlign="center">
                  {displayMedia.length} media item{displayMedia.length !== 1 ? 's' : ''} found
                </Text>
                <Text color={subtleTextColor} fontSize="xs" textAlign="center">
                  No images matching filters
                </Text>
              </Box>
            </AspectRatio>
          </Box>
        ) : null}

        {/* Media type labels */}
        {(photoCount > 0 || videoCount > 0 || has3DModel) && (
          <HStack
            position="absolute"
            top={3}
            left={3}
            zIndex={1}
            spacing={2}
          >
            {photoCount > 0 && (
              <Tag size="sm" bg="ancient.subtle" color="ancient.terracotta" borderRadius="full" boxShadow="sm">
                <TagLeftIcon as={ImageIcon} weight="bold" />
                <TagLabel>{showFilteredMedia ? carouselItems.filter(i => i.type === 'image').length : photoCount}</TagLabel>
              </Tag>
            )}
            {videoCount > 0 && (
              <Tag size="sm" bg="ancient.subtle" color="ancient.sky" /* Use a distinct color for video */ borderRadius="full" boxShadow="sm">
                <TagLeftIcon as={VideoCamera} weight="bold" />
                <TagLabel>{showFilteredMedia ? carouselItems.filter(i => i.type === 'video').length : videoCount}</TagLabel>
              </Tag>
            )}
            {has3DModel && (
              <Tag size="sm" bg="ancient.subtle" color="ancient.stone" borderRadius="full" boxShadow="sm">
                <TagLeftIcon as={Cube} weight="bold" />
                <TagLabel>3D</TagLabel>
              </Tag>
            )}
          </HStack>
        )}

        <Box p={5} bg={isMobile && isInInfoWindow ? useColorModeValue('white', 'gray.800') : bgColor}>
          <VStack align="stretch" spacing={4}>
            <VStack align="start" spacing={2}>
            <VStack align="start" spacing={0}>
              <Heading 
                size="md" 
                color={textColor} 
                noOfLines={1}
                fontFamily="'Cormorant Garamond', serif"
                fontWeight="600"
                letterSpacing="0.5px"
                position="relative"
                _after={{
                  content: '""',
                  position: 'absolute',
                  bottom: '-2px',
                  left: 0,
                  width: '40%',
                  height: '1px',
                  bg: accentColor,
                  backgroundImage: useColorModeValue(
                    "linear-gradient(90deg, var(--chakra-colors-ancient-terracotta), transparent)",
                    "linear-gradient(90deg, var(--chakra-colors-ancient-gold), transparent)"
                  )
                }}
              >
                {site.name}
              </Heading>
              
              {selectedSublocation && (
                <Text 
                  fontSize="md" 
                  color={accentColor} 
                  fontWeight="medium"
                  fontStyle="italic"
                >
                  {selectedSublocation.name}
                </Text>
              )}
            </VStack>
              
              <HStack spacing={2} flexWrap="wrap">
                {site.mysteryFlags?.length > 0 && (
              <Tag 
                bg="ancient.subtle" 
                color="ancient.gold" 
                size="sm" 
                borderRadius="full"
                boxShadow="0 0 0 1px rgba(0,0,0,0.1) inset"
                position="relative"
                overflow="hidden"
                _before={{
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundImage: "linear-gradient(135deg, rgba(255,255,255,0.2) 0%, transparent 50%)",
                  pointerEvents: 'none'
                }}
              >
                <TagLeftIcon as={Flag} weight="fill" />
                <TagLabel>Mystery</TagLabel>
              </Tag>
                )}
              </HStack>
            </VStack>
            
            {/* Location and Era Info */}
            <VStack align="stretch" spacing={2.5}>
              <VStack align="start" spacing={1}>
                <HStack>
                  <Icon as={MapPin} weight="fill" color={accentColor} boxSize={4} />
                  <Text
                    color={textColor}
                    noOfLines={1}
                    fontWeight="500"
                    fontFamily="'Cormorant Garamond', serif"
                  >
                    {
                      (site.location.region && site.location.country)
                        ? `${site.location.region}, ${site.location.country}`
                        : site.location.country || site.location.region || site.location.address // Fallback logic
                    }
               
                  </Text>
                </HStack>
                
                <HStack>
                  <Icon as={Calendar} weight="regular" color={subtleTextColor} boxSize={4} />
                  <Text 
                    color={subtleTextColor}
                    fontFamily="'Cormorant Garamond', serif"
                  >
                    {site.constructionPeriods?.[0] ? 
                      `${site.constructionPeriods[0].startEra || ''} ${site.constructionPeriods[0].endEra ? `- ${site.constructionPeriods[0].endEra}` : ''} ${site.constructionPeriods[0].culture ? `(${site.constructionPeriods[0].culture})` : ''}` 
                      : (site.estimatedEra || site.startEra || site.endEra || 'Era unknown')}
                  </Text>
                </HStack>
                
                <HStack>
                  <Icon as={MapTrifold} weight="regular" color={subtleTextColor} boxSize={4} />
                  <Text 
                    color={subtleTextColor} 
                    fontSize="sm"
                    fontFamily="'Cormorant Garamond', serif"
                  >
                    {displayCoordinatesString}
                  </Text>
                </HStack>
              </VStack>
            </VStack>

            {/* Description */}
            {(site.description || site.shortDescription) && (
              <>
                <Divider />
                <Text
                  color={textColor}
                  noOfLines={2}
                  fontSize="sm"
                  lineHeight="1.6"
                  fontFamily="'Cormorant Garamond', serif"
                  letterSpacing="0.2px"
                  position="relative"
                  pl={3}
                  _before={{
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: '2px',
                    bg: accentColor,
                    borderRadius: 'full'
                  }}
                >
                  {(() => {
                    const desc = site.description || site.shortDescription;
                    return typeof desc === 'object' && desc !== null && 'value' in desc
                      ? desc.value
                      : desc;
                  })()}
                </Text>
              </>
            )}
            
            {/* Features Section with Active Filters */}
            {(enabledFeatures.length > 0 || filters?.features?.length > 0) && (
              <>
                <Divider />
                <Box>
                  <Text 
                    fontWeight="600" 
                    mb={2} 
                    fontSize="sm" 
                    color={textColor}
                    fontFamily="'Cormorant Garamond', serif"
                    letterSpacing="0.5px"
                  >
                    Features:
                  </Text>
                  <Wrap spacing={2}>
                    {/* Show features from the site */}
                    {enabledFeatures.map(feature => (
                      <WrapItem key={feature.id} onClick={(e) => handleFeatureClick(e, feature)}>
                        <Tooltip 
                          label={isFeatureSelected(feature) 
                            ? `Remove "${getFeatureLabel(feature)}" filter` 
                            : `Add "${getFeatureLabel(feature)}" filter`} 
                          placement="top" 
                          hasArrow
                        >
                          <Badge 
                            bg={isFeatureSelected(feature) ? selectedBadgeBg : defaultBadgeBg}
                            color={isFeatureSelected(feature) ? badgeTextColor : 'ancient.terracotta'}
                            px={3}
                            py={1.5}
                            borderRadius="full"
                            cursor="pointer"
                            _hover={{
                              opacity: 0.9,
                              transform: 'scale(1.05)',
                              boxShadow: 'sm'
                            }}
                            transition="all 0.2s"
                            whiteSpace="normal"
                            textAlign="center"
                            boxShadow="sm"
                          >
                            {getFeatureLabel(feature)}
                          </Badge>
                        </Tooltip>
                      </WrapItem>
                    ))}
                  </Wrap>
                </Box>
              </>
            )}

            {/* Site Sublocations Section */}
            {site.siteSublocations && site.siteSublocations.length > 0 && (
              <>
                <Divider />
                <Box>
                  <Heading
                    size="sm"
                    mb={3}
                    color={textColor}
                    fontFamily="'Cormorant Garamond', serif"
                    letterSpacing="0.5px"
                  >
                    Sub-locations:
                  </Heading>
                  <VStack align="stretch" spacing={3}>
                    {site.siteSublocations.map(subLoc => {
                      const sLat = subLoc?.location?.coordinates?.lat;
                      const sLng = subLoc?.location?.coordinates?.lng;
                      let subDisplayCoords = 'Coords N/A';
                      if (typeof sLat === 'number' && typeof sLng === 'number') {
                          subDisplayCoords = `${sLat.toFixed(5)}, ${sLng.toFixed(5)}`;
                      }
                      return (
                        <Box
                          key={subLoc.id}
                          pl={3}
                          borderLeft="2px solid"
                          borderColor={useColorModeValue('ancient.terracottaAlpha', 'ancient.goldAlpha')}
                          bg={subtleBg}
                          p={3}
                          borderRadius="md"
                        >
                          <Heading
                            size="xs"
                            color={accentColor}
                            fontFamily="'Cormorant Garamond', serif"
                            mb={1}
                          >
                            {subLoc.name}
                          </Heading>
                          {subLoc.description && (
                            <Text fontSize="xs" color={subtleTextColor} noOfLines={2} fontFamily="'Cormorant Garamond', serif">
                              {subLoc.description}
                            </Text>
                          )}
                          {/* Display coordinates if available, using the calculated subDisplayCoords */}
                          {(typeof sLat === 'number' && typeof sLng === 'number') && (
                             <HStack mt={1}>
                               <Icon as={MapPin} weight="fill" color={subtleTextColor} boxSize={3} />
                               <Text fontSize="xs" color={subtleTextColor} fontFamily="'Cormorant Garamond', serif">
                                 {subDisplayCoords}
                               </Text>
                             </HStack>
                          )}
                        </Box>
                      );
                    })}
                  </VStack>
                </Box>
              </>
            )}
          </VStack>
        </Box>
      </Box>

      {/* Image Preview Modal */}
      <ImagePreviewModal
        isOpen={isPreviewOpen && currentItem?.type === 'image'} // Only open for images
        onClose={() => setIsPreviewOpen(false)}
        imageUrl={currentItem?.type === 'image' ? currentItem?.url : undefined}
        onPrevious={handlePrevImage}
        onNext={handleNextImage}
        hasMultiple={carouselItems.filter(item => item.type === 'image').length > 1} // Modal navigation for images only
        currentImage={currentItem?.type === 'image' ? {
          ...currentItem,
          siteId: site.id,
          siteLocation: site.location?.coordinates,
          subLocation: currentItem?.subLocation
        } : null}
      />
    </>
  );
}
export default SiteCardComponent;
